/**
 * Effective dealer-fee resolver (v5.5).
 *
 * Reads DB-backed system_settings.fees with config.js fallback. Keeps a
 * small cross-tab cache so the same payload isn't re-fetched on every
 * render. Use the `useFees()` hook in components, or `getFeesSync()` for
 * non-React code (falls back to config defaults only).
 */
import { useEffect, useState } from "react";
import { fees as CONFIG_FEES } from "../config";
import { api } from "./api";

const CACHE_KEY = "ssc_effective_fees_v1";
let cached = null;
let inFlight = null;

function readCache() {
  if (cached) return cached;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && typeof obj.doc_fee === "number") {
        cached = obj;
        return obj;
      }
    }
  } catch {}
  return null;
}

function writeCache(fees) {
  cached = fees;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(fees));
  } catch {}
}

export async function fetchFees() {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const { data } = await api.get("/system/fees");
      const fees = {
        doc_fee: typeof data.doc_fee === "number" ? data.doc_fee : CONFIG_FEES.docFee,
        title_fee: typeof data.title_fee === "number" ? data.title_fee : CONFIG_FEES.titleFee,
        fee_label_doc: data.fee_label_doc || "Documentation Fee",
        fee_label_title: data.fee_label_title || "Title Fee",
        source: data.source || "db",
      };
      writeCache(fees);
      return fees;
    } catch {
      const fallback = {
        doc_fee: CONFIG_FEES.docFee,
        title_fee: CONFIG_FEES.titleFee,
        fee_label_doc: "Documentation Fee",
        fee_label_title: "Title Fee",
        source: "config_default",
      };
      writeCache(fallback);
      return fallback;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function getFeesSync() {
  return (
    readCache() || {
      doc_fee: CONFIG_FEES.docFee,
      title_fee: CONFIG_FEES.titleFee,
      fee_label_doc: "Documentation Fee",
      fee_label_title: "Title Fee",
      source: "config_default",
    }
  );
}

export function useFees() {
  const [fees, setFees] = useState(() => getFeesSync());
  useEffect(() => {
    let on = true;
    fetchFees().then((f) => {
      if (on) setFees(f);
    });
    return () => {
      on = false;
    };
  }, []);
  return fees;
}
